import type { SupabaseClient } from '../../lib/supabase';
import type { TodayReport } from './analyzeState';
import type { ChangeRow } from './changeLog';
import { lastEntryDay } from './scoreboard';
import { trendSince, type TrendPayload, type TrendUnit } from './trends';

/** The parts of a saved report the reader shows. The Mac saves the headline and the checked text. */
export interface ReportBody {
  headline: string;
  text: string;
}
export interface SavedReport extends TodayReport {
  model: string;
  headline: string | null;
  finished_at: string | null;
  report: ReportBody | null;
}
export interface SectionChoice {
  id: string;
  name: string;
}
export interface ScoreV3Api {
  isTopMember(this: void): Promise<boolean>;
  reports(this: void): Promise<SavedReport[]>;
  /** Queues a request. The database checks who, the hours and the day. */
  analyze(this: void): Promise<void>;
  changeLog(this: void): Promise<ChangeRow[]>;
  sections(this: void): Promise<SectionChoice[]>;
  trend(this: void, section: string, unit: TrendUnit): Promise<TrendPayload>;
  /** The raw days of one section from a start day. Read only when a Details card is open. */
  days(this: void, section: string, since: string): Promise<TrendPayload>;
}

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('Unexpected Scoreboard response. Reload and try again.');
  return value as Record<string, unknown>;
}

export function readReportBody(value: unknown): ReportBody | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const r = value as Record<string, unknown>;
  if (typeof r.headline !== 'string' || typeof r.text !== 'string') return null;
  return { headline: r.headline, text: r.text };
}

/** What the database said to the button, in words a person can use. */
export function analyzeError(answer: unknown): string {
  const status = (answer as { status?: unknown } | null)?.status;
  if (status === 'not_allowed')
    return 'Only Kyu and the named top members can run the daily report.';
  if (status === 'closed') return 'Analyze is closed now. It opens at 6 AM ET.';
  if (status === 'used') return "Today's report has already been requested.";
  if (status === 'busy') return 'Another request is in line. Try again in a minute.';
  return 'The report could not be started. Try again.';
}

async function readDays(
  client: SupabaseClient,
  section: string,
  since: string,
): Promise<TrendPayload> {
  const { data, error } = await client.rpc('score_trend_days', {
    p_section: section,
    p_since: since,
  });
  if (error) throw error;
  const payload = object(data);
  if (
    typeof payload.today !== 'string' ||
    !Array.isArray(payload.metrics) ||
    !Array.isArray(payload.days)
  )
    throw new Error('Unexpected Trends response.');
  return payload as unknown as TrendPayload;
}

export function createScoreV3Api(session: SupabaseClient): ScoreV3Api {
  const client = session;
  return {
    async isTopMember() {
      const { data, error } = await client.rpc('score_is_top_member');
      if (error) throw error;
      return data === true;
    },
    async reports() {
      const { data, error } = await client
        .from('score_analysis_reports')
        .select(
          'id, report_day, status, started_by_name, started_at, picked_up_at, finished_at, headline, model, report, error',
        )
        .order('report_day', { ascending: false })
        .order('started_at', { ascending: false })
        .limit(60);
      if (error) throw error;
      return data.map((row) => ({
        id: row.id,
        report_day: row.report_day,
        status: row.status as SavedReport['status'],
        started_by_name: row.started_by_name,
        started_at: row.started_at,
        picked_up_at: row.picked_up_at,
        error: row.error,
        finished_at: row.finished_at,
        headline: row.headline,
        model: row.model,
        report: readReportBody(row.report),
      }));
    },
    async analyze() {
      const { data, error } = await client.rpc('score_analysis_request');
      if (error) throw new Error(analyzeError(null));
      const status = (object(data) as { status?: unknown }).status;
      if (status !== 'queued') throw new Error(analyzeError(data));
    },
    async changeLog() {
      const { data, error } = await client.rpc('score_change_log', {});
      if (error) throw error;
      if (!Array.isArray(data)) throw new Error('Unexpected Change log response.');
      return data as unknown as ChangeRow[];
    },
    async sections() {
      const { data, error } = await client
        .from('score_sections')
        .select('id, name')
        .eq('is_active', true)
        .order('position');
      if (error) throw error;
      return data;
    },
    async trend(section, unit) {
      // Ask for a window that reaches back far enough; the database reports its own Manila today.
      const today = new Date(Date.parse(`${lastEntryDay()}T00:00:00Z`) + 86_400_000)
        .toISOString()
        .slice(0, 10);
      return readDays(client, section, trendSince(today, unit));
    },
    days(section, since) {
      return readDays(client, section, since);
    },
  };
}
